import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';

export interface PlanEntitlement {
  enabled: boolean;
  limit: number | null;
}

export interface PlanCredit {
  amount: number;
  note: string;
}

export interface PricingPlan {
  _id: string;
  key: string;
  name: string;
  tagline: string;
  weight: number;
  includedCredits: number | null;
  displayFeatures: string[];
  /** null = quoted through sales (e.g. Enterprise's "Custom"), not free. */
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  currency: string;
  /**
   * A promotional discount off monthlyPrice/yearlyPrice, RESOLVED server-side
   * for this viewer (the credit service's own negotiated-rate, else per-plan,
   * else catalogue-wide discount) - not stored on the plan itself. Each axis
   * is independent: a promo may run monthly-only or yearly-only.
   */
  monthlyDiscountPercent: number | null;
  yearlyDiscountPercent: number | null;
  entitlements: Record<string, PlanEntitlement>;
  credits: Record<string, PlanCredit>;
  highlighted: boolean;
  cta: string;
  displayOrder: number;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface FeatureCatalogueEntry {
  key: string;
  label: string;
  description: string;
  kind: 'boolean' | 'limit';
  unit: string;
  /** Position on the quick-comparison table, ascending. null = not shown there. */
  comparisonOrder: number | null;
}

export interface BillingSettings {
  topUpPrice: number;
  topUpCredits: number;
  currency: string;
}

/**
 * What one action costs when the user's tier has no opinion about it.
 *
 * This is the price list the whole model falls back to, which is why a tier
 * going silent about a type means "charge this", never "free" — and why it, not
 * a plan's own (currently empty) `credits` map, is what a customer-facing
 * "where do my credits go" list has to read.
 */
export interface ServiceCreditPrice {
  _id: string;
  /** The service type being priced, e.g. "infyndEmail". */
  type: string;
  /** Credits moved per action. */
  amount: number;
  note: string;
  /** The feature this type is gated by, if any — links a price to a permission. */
  featureKey?: string;
}

export interface PricingBundle {
  plans: PricingPlan[];
  features: FeatureCatalogueEntry[];
  billingSettings: BillingSettings | null;
  serviceConfigs: ServiceCreditPrice[];
}

interface Envelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

/**
 * Pricing bundle API. One call fetches the sellable tiers, the feature
 * catalogue's display labels and the credits top-up rate from the Credit
 * Management service (via this app's Express middleware, which holds the
 * upstream API key).
 */
export const pricingApi = createApi({
  reducerPath: 'pricingApi',
  baseQuery: axiosBaseQuery('/api/pricing'),
  tagTypes: ['Pricing'],
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getPricingBundle: builder.query<PricingBundle, void>({
      query: () => '',
      transformResponse: (response: Envelope<PricingBundle>) =>
        response?.data ?? {
          plans: [],
          features: [],
          billingSettings: null,
          serviceConfigs: [],
        },
      providesTags: ['Pricing'],
      keepUnusedDataFor: 24 * 60 * 60, // 24 hours - pricing is relatively static
    }),
  }),
});

export const { useGetPricingBundleQuery } = pricingApi;

export default pricingApi;
